/**
 * Tipping utilities.
 *
 * Optional tip amount added to checkout.
 * Tip stored in protectedData.tip = { amount: number (cents), currency: string }
 *
 * Tip options: 10%, 15%, 20%, or custom amount.
 */

export const DEFAULT_TIP_PERCENTAGES = [10, 15, 20];
export const MIN_TIP_CENTS = 0;

/**
 * Calculate tip amount in cents from percentage.
 * @param {number} subtotalCents — subtotal in cents (before tip)
 * @param {number} percent — tip percentage (e.g. 15 for 15%)
 * @returns {number} — tip in cents (rounded)
 */
export const calculateTipFromPercent = (subtotalCents, percent) => {
  if (percent < 0) throw new Error('Tip percentage cannot be negative');
  return Math.round(subtotalCents * (percent / 100));
};

/**
 * Parse and validate a tip amount (custom input).
 * @param {string|number} value — raw input value (in major currency units, e.g. "5.50")
 * @param {string} currency — currency code
 * @returns {{ amountCents: number, error: string|null }}
 */
export const parseTipAmount = (value, currency = 'EUR') => {
  if (!value && value !== 0) return { amountCents: 0, error: null };
  const n = parseFloat(String(value).replace(',', '.'));
  if (isNaN(n)) return { amountCents: 0, error: 'Invalid tip amount' };
  if (n < 0) return { amountCents: 0, error: 'Tip cannot be negative' };
  const cents = Math.round(n * 100);
  return { amountCents: cents, error: null };
};

/**
 * Format tip for display.
 * @param {number} amountCents
 * @param {string} currency
 * @returns {string} — e.g. "€5.00"
 */
export const formatTip = (amountCents, currency = 'EUR') => {
  const symbols = { EUR: '€', USD: '$', GBP: '£' };
  const symbol = symbols[currency] || currency + ' ';
  return `${symbol}${(amountCents / 100).toFixed(2)}`;
};

/**
 * Get tip from transaction.
 * @param {Object} transaction — Sharetribe transaction
 * @returns {{ amount: number, currency: string } | null}
 */
export const getTip = (transaction) =>
  transaction?.attributes?.protectedData?.tip || null;
